import { parseEffort, fmtMinutes } from './helpers';

// ─── Auto-scheduling algorithm ──────────────────────────────────────────────
export const autoScheduleTasks = (calendarEvents, openTasks, nowMin, workStart = 9, workEnd = 18) => {
  const dayStartMin = workStart * 60;
  const dayEndMin = workEnd * 60;
  const currentMin = nowMin || (new Date().getHours() * 60 + new Date().getMinutes());

  const sorted = [...calendarEvents].sort((a, b) => {
    const [ah, am] = a.time.split(':').map(Number);
    const [bh, bm] = b.time.split(':').map(Number);
    return (ah * 60 + am) - (bh * 60 + bm);
  });

  const busyBlocks = sorted.map((e, i) => {
    const [h, m] = e.time.split(':').map(Number);
    const startMin = h * 60 + m;
    let endMin;
    if (e.endTime) {
      const [eh, em] = e.endTime.split(':').map(Number);
      endMin = eh * 60 + em;
    } else {
      let duration = 60;
      if (i < sorted.length - 1) {
        const [nh, nm] = sorted[i + 1].time.split(':').map(Number);
        const gap = (nh * 60 + nm) - startMin;
        if (gap > 0 && gap <= 180) duration = gap;
      }
      duration = Math.min(duration, 120);
      endMin = startMin + duration;
    }
    return { startMin, endMin, type: 'event', item: e };
  });

  const scoredTasks = openTasks
    .filter(t => !t.done)
    .map(t => {
      const effort = parseEffort(t.effort);
      let score = 0;
      score += (t.priority === 1 ? 3000 : t.priority === 2 ? 2000 : 1000);
      // Goal priority cascade
      const gp = t.goals?.priority || 3;
      if (gp === 1) score += 800;       // Critical goal
      else if (gp === 2) score += 400;  // High goal
      else if (gp === 4) score -= 200;  // Low goal
      // Deadline urgency
      if (t.due_label === 'Today' || t.due_label === 'Overdue') score += 500;
      else if (t.due_label === 'Tomorrow') score += 300;
      else if (t.due_label && t.due_label.match(/^\dd$/)) score += 200;
      if (effort <= 30) score += 50;
      else if (effort <= 60) score += 20;
      return { ...t, effortMin: effort, score };
    })
    .sort((a, b) => {
      // Manual queue rank (tasks.sort_order >= 1, set by dragging Up Next)
      // wins outright — mirrors scheduler-engine.js rankTasks.
      const mA = a.sort_order >= 1 ? a.sort_order : null;
      const mB = b.sort_order >= 1 ? b.sort_order : null;
      if (mA !== null || mB !== null) {
        if (mA === null) return 1;
        if (mB === null) return -1;
        if (mA !== mB) return mA - mB;
      }
      return b.score - a.score;
    });

  const scheduleFrom = Math.max(dayStartMin, currentMin);
  const availableSlots = [];
  let cursor = scheduleFrom;

  for (const block of busyBlocks) {
    if (block.endMin <= scheduleFrom) continue;
    const blockStart = Math.max(block.startMin, scheduleFrom);
    if (blockStart > cursor) {
      availableSlots.push({ startMin: cursor, endMin: blockStart, remaining: blockStart - cursor });
    }
    cursor = Math.max(cursor, block.endMin);
  }
  if (cursor < dayEndMin) {
    availableSlots.push({ startMin: cursor, endMin: dayEndMin, remaining: dayEndMin - cursor });
  }

  const scheduled = [];
  const overflow = [];

  for (const block of busyBlocks) {
    scheduled.push(block);
  }

  for (const task of scoredTasks) {
    let placed = false;
    for (const slot of availableSlots) {
      if (slot.remaining <= 0) continue;
      const minBlock = 15;
      if (slot.remaining >= minBlock) {
        const duration = Math.min(task.effortMin, slot.remaining);
        scheduled.push({
          startMin: slot.endMin - slot.remaining,
          endMin: slot.endMin - slot.remaining + duration,
          type: 'task',
          item: task,
          duration,
          truncated: duration < task.effortMin,
        });
        slot.remaining -= duration;
        placed = true;
        break;
      }
    }
    if (!placed) {
      overflow.push(task);
    }
  }

  scheduled.sort((a, b) => a.startMin - b.startMin);
  return { scheduled, overflow };
};
